import { AutomationExecutionStatusEnum } from '@repo/utils';
import { useReactFlow } from '@xyflow/react';
import {
  automationModeSelector,
  automationReportDataSelector,
  isDebugModeSelector,
  reportExecutionIdSelector,
} from 'automations/selectors/automationConfig';
import { useGetAutomationExecutionQuery, useGetAutomationTreeQuery } from 'automations/services/automationService';
import { setAutomationReportData } from 'automations/store/automationConfigSlice';
import { addInitialData } from 'automations/store/reactFlowSlice';
import { AutomationMode } from 'automations/types/automation';
import { parseAutomationTree } from 'automations/utils/parseAutomationTree';
import { parseAutomationExecution } from 'automations/utils/parseExecutionTree';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAutomationDispatch } from './useAutomationDispatch';
import useAutomationId from './useAutomationId';

const DEFAULT_POLLING_INTERVAL = 30 * 1000;
const DEBUG_POLLING_INTERVAL = 10 * 1000;

export default function useNodesInjector() {
  const dispatch = useAutomationDispatch();
  const automationId = useAutomationId();
  const { fitView } = useReactFlow();
  const automationMode = useSelector(automationModeSelector);
  const automationExecutionId = useSelector(reportExecutionIdSelector);
  const debugModeSelector = useSelector(isDebugModeSelector);
  const reportData = useSelector(automationReportDataSelector);

  const automationExecutionPollingInterval = useMemo(() => {
    if (reportData?.status !== AutomationExecutionStatusEnum.RUNNING) {
      return 0;
    } else if (debugModeSelector) {
      return DEBUG_POLLING_INTERVAL;
    }
    return DEFAULT_POLLING_INTERVAL;
  }, [debugModeSelector, reportData?.status]);

  const {
    currentData: data,
    isFetching,
    isError,
  } = useGetAutomationTreeQuery({
    id: parseInt(automationId, 10),
    withSuccessExecutionCounts: automationMode === AutomationMode.commonReport,
  }, {
    skip: !automationId || automationMode === AutomationMode.individualReport,
    refetchOnMountOrArgChange: true,
  });

  const {
    currentData: executionData,
    isFetching: isExecutionFetching,
    isError: isExecutionError,
  } = useGetAutomationExecutionQuery(automationExecutionId!, {
    skip: !automationExecutionId || automationMode !== AutomationMode.individualReport,
    pollingInterval: automationExecutionPollingInterval,
  });

  useEffect(() => {
    if (
      automationMode === AutomationMode.individualReport
      && executionData?.automationExecutions?.automationExecutions?.length
    ) {
      dispatch(setAutomationReportData(executionData.automationExecutions.automationExecutions[0]!));

      const { nodes, edges } = parseAutomationExecution(executionData.automationExecutions.automationExecutions[0]!);
      dispatch(addInitialData({ nodes, edges }));

      setTimeout(() => {
        fitView({
          padding: 3,
          duration: 300,
        });
      }, 0);
    }
  }, [automationMode, executionData, dispatch, fitView]);

  useEffect(() => {
    if (automationMode === AutomationMode.individualReport) return;

    if (data?.automationTree.length) {
      const { nodes, edges } = parseAutomationTree(data.automationTree);
      dispatch(addInitialData({ nodes, edges }));

      setTimeout(() => {
        fitView({
          padding: 3,
          duration: 300,
          // add focus on trigger node
          nodes: [{ id: nodes[0]?.id ?? '' }],
        });
      }, 0);
    } else {
      setTimeout(() => {
        fitView({
          padding: 3,
          duration: 300,
        });
      }, 0);
    }
  }, [automationMode, data, dispatch, fitView]);

  return {
    isFetching: isFetching || isExecutionFetching,
    isError: isError || isExecutionError,
  };
}
